import { useState, useEffect, useCallback, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Plus, Users } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Badge,
  Button,
  Card,
  Checkbox,
  DataTable,
  ErrorState,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  SelectRoot,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  useToast,
} from '@/components/ui'
import type { ColumnDef } from '@tanstack/react-table'
import { useAuthStore } from '@/stores/auth.ts'

// ─── Types ────────────────────────────────────────────────────────────────────

interface ShiftRule {
  id: number
  definitionId: number
  isPending: boolean
  weekdays: string[]
  startTime: string
  endTime: string
  minimumEmployees: number
  maximumEmployees: number
}

interface ServiceDefinition {
  id: number
  name: string
  active: boolean
  confirmedPlanningAdditionalId: number | null
  requiredDepartmentIds: number[]
  combineWithOtherServicesSameDay: boolean
  allowMultipleShiftsSameServiceSameDay: boolean
  hasPendingChanges: boolean
  shiftRules: ShiftRule[]
  pendingShiftRules: ShiftRule[]
}

interface Department {
  id: number
  name: string
}

interface CustomType {
  id: number
  name: string
  type: string
  active: boolean
}

interface TanssData {
  departments: Department[]
  customTypes: CustomType[]
}

interface TechnicianPreview {
  count: number
  exampleNames: string[]
  debug?: {
    totalTechnicians: number
    activeTechnicians: number
    technicianIdsInErpMap: number
    technicianIdsNotInErpMap: number
    requestedDeptIds: number[]
    matchBreakdown: Array<{ name: string; id: number; hasDepts: boolean; deptIds: number[]; qualifies: boolean }>
  }
}

// ─── Shift rule form item ─────────────────────────────────────────────────────

interface ShiftRuleForm {
  _key: string
  weekdays: string[]
  startTime: string
  endTime: string
  minimumEmployees: number
  maximumEmployees: number
}

interface EditForm {
  name: string
  active: boolean
  confirmedPlanningAdditionalId: number | null
  requiredDepartmentIds: number[]
  combineWithOtherServicesSameDay: boolean
  allowMultipleShiftsSameServiceSameDay: boolean
  shiftRules: ShiftRuleForm[]
}

// ─── Constants ────────────────────────────────────────────────────────────────

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']

const CUSTOM_TYPE_NONE = '__none__'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function extractErrorMessage(err: unknown): string {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
    (err as Error)?.message ??
    String(err)
  )
}

let _keyCounter = 0
function nextKey(): string {
  return String(++_keyCounter)
}

function defaultForm(): EditForm {
  return {
    name: '',
    active: true,
    confirmedPlanningAdditionalId: null,
    requiredDepartmentIds: [],
    combineWithOtherServicesSameDay: true,
    allowMultipleShiftsSameServiceSameDay: false,
    shiftRules: [],
  }
}

function definitionToForm(def: ServiceDefinition): EditForm {
  // initialise editor with pending rules if they exist, else active rules
  const sourceRules = def.pendingShiftRules.length > 0 ? def.pendingShiftRules : def.shiftRules
  return {
    name: def.name,
    active: def.active,
    confirmedPlanningAdditionalId: def.confirmedPlanningAdditionalId,
    requiredDepartmentIds: def.requiredDepartmentIds,
    combineWithOtherServicesSameDay: def.combineWithOtherServicesSameDay,
    allowMultipleShiftsSameServiceSameDay: def.allowMultipleShiftsSameServiceSameDay,
    shiftRules: sourceRules.map((r) => ({
      _key: nextKey(),
      weekdays: r.weekdays,
      startTime: r.startTime,
      endTime: r.endTime,
      minimumEmployees: r.minimumEmployees,
      maximumEmployees: r.maximumEmployees,
    })),
  }
}

function validateShiftRule(rule: ShiftRuleForm): string[] {
  const errors: string[] = []
  if (rule.weekdays.length === 0) errors.push('Mindestens 1 Wochentag erforderlich')
  if (!rule.startTime || !rule.endTime) {
    errors.push('Start- und Endzeit erforderlich')
  } else if (rule.startTime >= rule.endTime) {
    errors.push('Endzeit muss nach Startzeit liegen')
  }
  if (!Number.isFinite(rule.minimumEmployees) || rule.minimumEmployees < 1)
    errors.push('Mindestbesetzung ≥ 1')
  if (!Number.isFinite(rule.maximumEmployees) || rule.maximumEmployees < rule.minimumEmployees)
    errors.push('Maximalbesetzung ≥ Mindestbesetzung')
  return errors
}

// ─── ShiftRuleRow ─────────────────────────────────────────────────────────────

function ShiftRuleRow({
  rule,
  onChange,
  onRemove,
}: {
  rule: ShiftRuleForm
  onChange: (updated: ShiftRuleForm) => void
  onRemove: () => void
}) {
  const errors = validateShiftRule(rule)

  const toggleWeekday = (day: string) => {
    const next = rule.weekdays.includes(day)
      ? rule.weekdays.filter((d) => d !== day)
      : [...rule.weekdays, day]
    onChange({ ...rule, weekdays: next })
  }

  return (
    <div
      className={`border rounded-md p-3 space-y-3 ${
        errors.length > 0 ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">Schicht</span>
        <Button variant="linkDanger" size="xs" onClick={onRemove}>
          Entfernen
        </Button>
      </div>

      {/* Weekday toggles */}
      <div>
        <p className="text-xs text-gray-500 mb-1">Wochentage</p>
        <div className="flex gap-1 flex-wrap">
          {WEEKDAYS.map((day) => (
            <Button
              key={day}
              variant={rule.weekdays.includes(day) ? 'primary' : 'secondary'}
              size="xs"
              onClick={() => toggleWeekday(day)}
            >
              {day}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Startzeit"
          type="time"
          value={rule.startTime}
          onChange={(e) => onChange({ ...rule, startTime: e.target.value })}
        />
        <Input
          label="Endzeit"
          type="time"
          value={rule.endTime}
          onChange={(e) => onChange({ ...rule, endTime: e.target.value })}
        />
        <Input
          label="Min. Besetzung"
          type="number"
          min={1}
          value={rule.minimumEmployees}
          onChange={(e) => {
            const v = Math.max(1, parseInt(e.target.value, 10) || 1)
            onChange({ ...rule, minimumEmployees: v, maximumEmployees: Math.max(rule.maximumEmployees, v) })
          }}
        />
        <Input
          label="Max. Besetzung"
          type="number"
          min={rule.minimumEmployees}
          value={rule.maximumEmployees}
          onChange={(e) => {
            const v = Math.max(rule.minimumEmployees, parseInt(e.target.value, 10) || rule.minimumEmployees)
            onChange({ ...rule, maximumEmployees: v })
          }}
        />
      </div>

      {errors.length > 0 && (
        <ul className="text-xs text-red-600 space-y-0.5">
          {errors.map((e) => (
            <li key={e}>• {e}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ─── ShiftRulesDisplay (read-only) ───────────────────────────────────────────

function ShiftRulesDisplay({ rules }: { rules: ShiftRule[] }) {
  if (rules.length === 0) {
    return <p className="text-xs text-gray-400 italic">Keine Schichtregeln</p>
  }
  return (
    <div className="space-y-2">
      {rules.map((r) => (
        <div key={r.id} className="text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded p-2">
          <span className="font-medium">{r.weekdays.join(', ')}</span>
          {' · '}
          {r.startTime}–{r.endTime}
          {' · '}
          {r.minimumEmployees}–{r.maximumEmployees} Personen
        </div>
      ))}
    </div>
  )
}

// ─── EditDialog ───────────────────────────────────────────────────────────────

function EditDialog({
  open,
  definition,
  tanssData,
  tanssLoading,
  tanssError,
  onClose,
  onSave,
  isSaving,
}: {
  open: boolean
  definition: ServiceDefinition | null
  tanssData: TanssData | undefined
  tanssLoading: boolean
  tanssError: boolean
  onClose: () => void
  onSave: (form: EditForm) => void
  isSaving: boolean
}) {
  const isCreate = definition === null

  const [form, setForm] = useState<EditForm>(defaultForm)
  const [preview, setPreview] = useState<TechnicianPreview | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (open) {
      setForm(definition ? definitionToForm(definition) : defaultForm())
      setPreview(null)
    }
  }, [open, definition])

  // Live technician preview when departments change
  const fetchPreview = useCallback(async (deptIds: number[]) => {
    setPreviewLoading(true)
    try {
      const qs = deptIds.length > 0 ? `?departmentIds=${deptIds.join(',')}` : ''
      const res = await apiClient.get<TechnicianPreview>(`/dispo/service-definitions/technician-preview${qs}`)
      setPreview(res.data)
    } catch {
      setPreview(null)
    } finally {
      setPreviewLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!open) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      void fetchPreview(form.requiredDepartmentIds)
    }, 400)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [form.requiredDepartmentIds, open, fetchPreview])

  const departments = tanssData?.departments ?? []
  const customTypes = tanssData?.customTypes ?? []
  const customCustomTypes = customTypes.filter((t) => t.type === 'CUSTOM')
  const activeCustomTypes = customCustomTypes.filter((t) => t.active)

  // Detect if current confirmedPlanningAdditionalId references an inactive type
  const currentConfirmedType = form.confirmedPlanningAdditionalId !== null
    ? customCustomTypes.find((t) => t.id === form.confirmedPlanningAdditionalId)
    : null
  const confirmedTypeIsInactive = currentConfirmedType !== null && currentConfirmedType !== undefined && !currentConfirmedType.active

  const allShiftRulesValid = form.shiftRules.every((r) => validateShiftRule(r).length === 0)
  const canSave = form.name.trim().length > 0 && allShiftRulesValid && !tanssError

  const handleToggleDept = (id: number) => {
    setForm((f) => ({
      ...f,
      requiredDepartmentIds: f.requiredDepartmentIds.includes(id)
        ? f.requiredDepartmentIds.filter((d) => d !== id)
        : [...f.requiredDepartmentIds, id],
    }))
  }

  const handleAddShiftRule = () => {
    setForm((f) => ({
      ...f,
      shiftRules: [
        ...f.shiftRules,
        {
          _key: nextKey(),
          weekdays: ['Mo', 'Di', 'Mi', 'Do', 'Fr'],
          startTime: '08:00',
          endTime: '17:00',
          minimumEmployees: 1,
          maximumEmployees: 1,
        },
      ],
    }))
  }

  const handleRemoveShiftRule = (key: string) => {
    setForm((f) => ({ ...f, shiftRules: f.shiftRules.filter((r) => r._key !== key) }))
  }

  const handleChangeShiftRule = (key: string, updated: ShiftRuleForm) => {
    setForm((f) => ({
      ...f,
      shiftRules: f.shiftRules.map((r) => (r._key === key ? updated : r)),
    }))
  }

  const handleConfirmedTypeChange = (val: string) => {
    if (val === CUSTOM_TYPE_NONE) {
      setForm((f) => ({ ...f, confirmedPlanningAdditionalId: null }))
    } else {
      setForm((f) => ({ ...f, confirmedPlanningAdditionalId: parseInt(val, 10) }))
    }
  }

  const hasPendingChanges = definition?.hasPendingChanges ?? false
  const hasActiveRules = (definition?.shiftRules ?? []).length > 0
  const hasPendingRules = (definition?.pendingShiftRules ?? []).length > 0

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isCreate ? 'Neue Dienstdefinition' : `Dienstdefinition bearbeiten – ${definition?.name ?? ''}`}
      size="xl"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={isSaving}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            onClick={() => onSave(form)}
            loading={isSaving}
            disabled={!canSave || isSaving}
          >
            {isCreate ? 'Erstellen' : 'Speichern'}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {tanssLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span className="animate-spin inline-block w-4 h-4 border-2 border-gray-300 border-t-brand-700 rounded-full" />
            TANSS-Daten werden geladen…
          </div>
        )}
        {tanssError && (
          <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              TANSS-Daten konnten nicht geladen werden. Departments und Custom-Typen sind nicht verfügbar.
              Speichern ist nur möglich, wenn TANSS erreichbar ist.
            </span>
          </div>
        )}

        {/* Name + Active */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Input
              label="Name"
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              error={form.name.trim().length === 0 ? 'Name ist erforderlich' : undefined}
            />
          </div>
          <div className="flex items-end pb-1">
            <Switch
              checked={form.active}
              onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              label="Aktiv"
              hint="Deaktivierte Definitionen werden bei der Planung ignoriert"
            />
          </div>
        </div>

        {/* Confirmed Custom Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Bestätigter Custom-Typ (TANSS)
          </label>
          <SelectRoot
            value={
              form.confirmedPlanningAdditionalId !== null
                ? String(form.confirmedPlanningAdditionalId)
                : CUSTOM_TYPE_NONE
            }
            onValueChange={handleConfirmedTypeChange}
            disabled={tanssError || tanssLoading}
          >
            <SelectTrigger className="w-full max-w-sm">
              <SelectValue placeholder="— Keinen Typ wählen —" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={CUSTOM_TYPE_NONE}>— Keinen Typ wählen —</SelectItem>
              {activeCustomTypes.map((t) => (
                <SelectItem key={t.id} value={String(t.id)}>
                  {t.name}
                </SelectItem>
              ))}
              {/* Show inactive type if it's currently selected */}
              {confirmedTypeIsInactive && (
                <SelectItem value={String(form.confirmedPlanningAdditionalId!)} disabled>
                  {currentConfirmedType!.name} (deaktiviert in TANSS)
                </SelectItem>
              )}
            </SelectContent>
          </SelectRoot>
          {confirmedTypeIsInactive && (
            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Dieser Typ ist in TANSS deaktiviert. Für neue Definitionen bitte einen aktiven Typ wählen.
            </p>
          )}
        </div>

        {/* Departments */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Abteilungen (Departments)
          </label>
          {tanssLoading ? (
            <p className="text-xs text-gray-400">Abteilungen werden geladen…</p>
          ) : tanssError ? (
            <p className="text-xs text-amber-600">Abteilungen nicht verfügbar</p>
          ) : departments.length === 0 ? (
            <p className="text-xs text-gray-400">Keine Abteilungen gefunden</p>
          ) : (
            <div className="border border-gray-200 rounded-md max-h-40 overflow-y-auto p-2 space-y-1">
              {departments.map((d) => (
                <Checkbox
                  key={d.id}
                  label={d.name}
                  checked={form.requiredDepartmentIds.includes(d.id)}
                  onChange={() => handleToggleDept(d.id)}
                  wrapperClassName="hover:bg-gray-50 p-1 rounded w-full"
                />
              ))}
            </div>
          )}

          {/* Technician Preview */}
          <div className="mt-2">
            {previewLoading ? (
              <p className="text-xs text-gray-400">Techniker-Vorschau wird berechnet…</p>
            ) : preview !== null ? (
              <div className="space-y-1">
                <div
                  className={`flex items-start gap-2 text-xs rounded p-2 ${
                    preview.count === 0
                      ? 'bg-amber-50 text-amber-700 border border-amber-200'
                      : 'bg-green-50 text-green-700 border border-green-200'
                  }`}
                >
                  <Users className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>
                    {preview.count === 0 ? (
                      'Kein Techniker erfüllt alle ausgewählten Abteilungs-Anforderungen'
                    ) : (
                      <>
                        {preview.count} Techniker qualifiziert
                        {preview.exampleNames.length > 0 && (
                          <> · z.B. {preview.exampleNames.join(', ')}</>
                        )}
                      </>
                    )}
                  </span>
                </div>
                {preview.count === 0 && preview.debug && (
                  <details className="text-xs text-gray-500 border border-gray-200 rounded p-2 bg-gray-50">
                    <summary className="cursor-pointer font-medium text-gray-600">Diagnose-Details</summary>
                    <div className="mt-2 space-y-1">
                      <p>Gesamt-Techniker in TANSS: <strong>{preview.debug.totalTechnicians}</strong></p>
                      <p>Davon verfügbar (nicht explizit deaktiviert): <strong>{preview.debug.activeTechnicians}</strong></p>
                      <p>Verfügbare Techniker mit ERP-Abteilungsdaten: <strong>{preview.debug.technicianIdsInErpMap}</strong></p>
                      <p>Verfügbare Techniker OHNE ERP-Abteilungsdaten: <strong>{preview.debug.technicianIdsNotInErpMap}</strong></p>
                      <p>Gesuchte Abteilungs-IDs: <strong>{preview.debug.requestedDeptIds.join(', ')}</strong></p>
                      {preview.debug.matchBreakdown.length > 0 && (
                        <div className="mt-2">
                          <p className="font-medium">Erste aktive Techniker (max. 20):</p>
                          <table className="mt-1 w-full text-xs">
                            <thead>
                              <tr className="text-left text-gray-400">
                                <th className="pr-2">Name</th>
                                <th className="pr-2">ID</th>
                                <th className="pr-2">Abt.-IDs aus ERP</th>
                                <th>Qualifiziert</th>
                              </tr>
                            </thead>
                            <tbody>
                              {preview.debug.matchBreakdown.map((row) => (
                                <tr key={row.id} className={row.qualifies ? 'text-green-700' : ''}>
                                  <td className="pr-2">{row.name}</td>
                                  <td className="pr-2">{row.id}</td>
                                  <td className="pr-2">{row.hasDepts ? row.deptIds.join(', ') : '–'}</td>
                                  <td>{row.qualifies ? '✓' : '✗'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </details>
                )}
              </div>
            ) : null}
          </div>
        </div>

        {/* Combine + Multiple switches */}
        <div className="space-y-3">
          <Switch
            checked={form.combineWithOtherServicesSameDay}
            onChange={(e) => setForm((f) => ({ ...f, combineWithOtherServicesSameDay: e.target.checked }))}
            label="Mit anderen Diensten am gleichen Tag kombinierbar"
          />
          <Switch
            checked={form.allowMultipleShiftsSameServiceSameDay}
            onChange={(e) => setForm((f) => ({ ...f, allowMultipleShiftsSameServiceSameDay: e.target.checked }))}
            label="Mehrere Schichten dieses Dienstes am gleichen Tag erlaubt"
          />
        </div>

        {/* Pending vs Active shift rules display (only for existing with pending changes) */}
        {!isCreate && hasPendingChanges && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Aktuelle Konfiguration</h4>
              {hasActiveRules ? (
                <ShiftRulesDisplay rules={definition!.shiftRules} />
              ) : (
                <p className="text-xs text-gray-400 italic">Keine aktiven Regeln</p>
              )}
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Ausstehende Konfiguration</h4>
              {hasPendingRules ? (
                <ShiftRulesDisplay rules={definition!.pendingShiftRules} />
              ) : (
                <p className="text-xs text-gray-400 italic">Keine ausstehenden Regeln</p>
              )}
            </div>
          </div>
        )}

        {/* Shift rules editor */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700">
              {isCreate
                ? 'Schichtregeln'
                : hasPendingChanges
                ? 'Neue ausstehende Konfiguration bearbeiten'
                : 'Schichtregeln (werden am nächsten Freitagslauf aktiv)'}
            </h4>
            <Button variant="secondary" size="sm" onClick={handleAddShiftRule} className="gap-1">
              <Plus className="w-3.5 h-3.5" />
              Schicht hinzufügen
            </Button>
          </div>
          {form.shiftRules.length === 0 ? (
            <p className="text-xs text-gray-400 italic">Noch keine Schichtregeln definiert</p>
          ) : (
            <div className="space-y-3">
              {form.shiftRules.map((rule) => (
                <ShiftRuleRow
                  key={rule._key}
                  rule={rule}
                  onChange={(updated) => handleChangeShiftRule(rule._key, updated)}
                  onRemove={() => handleRemoveShiftRule(rule._key)}
                />
              ))}
            </div>
          )}
          {!allShiftRulesValid && form.shiftRules.length > 0 && (
            <p className="text-xs text-red-600 mt-2">
              Bitte alle Schichtregeln korrigieren, bevor gespeichert werden kann.
            </p>
          )}
        </div>
      </div>
    </Modal>
  )
}

// ─── DataTable columns ────────────────────────────────────────────────────────

function buildColumns(
  departments: Department[],
  customTypes: CustomType[],
): ColumnDef<ServiceDefinition>[] {
  return [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <span className="font-medium text-gray-900">{row.original.name}</span>
      ),
    },
    {
      accessorKey: 'active',
      header: 'Status',
      size: 110,
      cell: ({ row }) => (
        <Badge variant={row.original.active ? 'success' : 'neutral'} size="sm">
          {row.original.active ? 'Aktiv' : 'Deaktiviert'}
        </Badge>
      ),
    },
    {
      accessorKey: 'hasPendingChanges',
      header: 'Ausstehend',
      size: 130,
      cell: ({ row }) =>
        row.original.hasPendingChanges ? (
          <Badge variant="warning" size="sm">
            Ausstehend
          </Badge>
        ) : (
          <span className="text-xs text-gray-400">–</span>
        ),
    },
    {
      accessorKey: 'confirmedPlanningAdditionalId',
      header: 'Custom-Typ',
      cell: ({ row }) => {
        const typeId = row.original.confirmedPlanningAdditionalId
        if (typeId === null) return <span className="text-xs text-gray-400">–</span>
        const t = customTypes.find((ct) => ct.id === typeId)
        return (
          <span className={`text-sm ${t && !t.active ? 'text-amber-600 italic' : 'text-gray-700'}`}>
            {t ? t.name : `ID ${typeId}`}
            {t && !t.active && ' (deaktiviert)'}
          </span>
        )
      },
    },
    {
      accessorKey: 'requiredDepartmentIds',
      header: 'Abteilungen',
      cell: ({ row }) => {
        const ids = row.original.requiredDepartmentIds
        if (ids.length === 0) return <span className="text-xs text-gray-400">–</span>
        const names = ids.map((id) => departments.find((d) => d.id === id)?.name ?? `#${id}`)
        return <span className="text-sm text-gray-700">{names.join(', ')}</span>
      },
    },
  ]
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function DispoServiceDefinitionsPage() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const currentUser = useAuthStore((s) => s.user)
  const isAdmin = currentUser?.role === 'admin'

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingDef, setEditingDef] = useState<ServiceDefinition | null>(null)

  const [proposalTypeValue, setProposalTypeValue] = useState<string>(CUSTOM_TYPE_NONE)

  // ─── Queries ──────────────────────────────────────────────────────────────

  const definitionsQuery = useQuery<ServiceDefinition[]>({
    queryKey: ['dispo-service-definitions'],
    queryFn: async () => {
      const res = await apiClient.get('/dispo/service-definitions')
      return res.data
    },
    enabled: isAdmin,
  })

  const tanssQuery = useQuery<TanssData>({
    queryKey: ['dispo-service-definitions-tanss-data'],
    queryFn: async () => {
      const res = await apiClient.get('/dispo/service-definitions/tanss-data')
      return res.data
    },
    enabled: isAdmin,
    staleTime: 2 * 60 * 1000,
  })

  const settingsQuery = useQuery<{ proposalPlanningAdditionalId: number | null }>({
    queryKey: ['dispo-settings'],
    queryFn: async () => {
      const res = await apiClient.get('/dispo/settings')
      return res.data
    },
    enabled: isAdmin,
  })

  useEffect(() => {
    if (settingsQuery.data) {
      setProposalTypeValue(
        settingsQuery.data.proposalPlanningAdditionalId !== null
          ? String(settingsQuery.data.proposalPlanningAdditionalId)
          : CUSTOM_TYPE_NONE,
      )
    }
  }, [settingsQuery.data])

  // ─── Mutations ────────────────────────────────────────────────────────────

  const saveSettingsMutation = useMutation({
    mutationFn: async (proposalPlanningAdditionalId: number | null) => {
      await apiClient.patch('/dispo/settings', { proposalPlanningAdditionalId })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispo-settings'] })
      toast({ title: 'Planungseinstellungen gespeichert', variant: 'success' })
    },
    onError: (err) => {
      toast({ title: 'Fehler', description: extractErrorMessage(err), variant: 'error' })
    },
  })

  const saveMutation = useMutation({
    mutationFn: async ({ id, form }: { id: number | null; form: EditForm }) => {
      const payload = {
        name: form.name.trim(),
        active: form.active,
        confirmedPlanningAdditionalId: form.confirmedPlanningAdditionalId,
        requiredDepartmentIds: form.requiredDepartmentIds,
        combineWithOtherServicesSameDay: form.combineWithOtherServicesSameDay,
        allowMultipleShiftsSameServiceSameDay: form.allowMultipleShiftsSameServiceSameDay,
        shiftRules: form.shiftRules.map((r) => ({
          weekdays: r.weekdays,
          startTime: r.startTime,
          endTime: r.endTime,
          minimumEmployees: r.minimumEmployees,
          maximumEmployees: r.maximumEmployees,
        })),
      }
      if (id === null) {
        const res = await apiClient.post('/dispo/service-definitions', payload)
        return res.data
      } else {
        const res = await apiClient.patch(`/dispo/service-definitions/${id}`, payload)
        return res.data
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispo-service-definitions'] })
      setDialogOpen(false)
      toast({
        title: 'Änderungen gespeichert — werden beim nächsten Freitagslauf aktiv',
        variant: 'success',
      })
    },
    onError: (err) => {
      const msg = extractErrorMessage(err)
      const isConflict = (err as { response?: { status?: number } })?.response?.status === 409
      toast({
        title: isConflict ? 'Löschen nicht möglich' : 'Fehler beim Speichern',
        description: isConflict
          ? 'Diese Definition besitzt TANSS-Einträge und kann nur deaktiviert, nicht gelöscht werden.'
          : msg,
        variant: 'error',
      })
    },
  })

  // ─── Handlers ────────────────────────────────────────────────────────────

  const handleOpenCreate = () => {
    setEditingDef(null)
    setDialogOpen(true)
  }

  const handleOpenEdit = (def: ServiceDefinition) => {
    setEditingDef(def)
    setDialogOpen(true)
  }

  const handleSaveProposalType = () => {
    const idValue =
      proposalTypeValue === CUSTOM_TYPE_NONE ? null : parseInt(proposalTypeValue, 10)
    saveSettingsMutation.mutate(idValue)
  }

  const handleSaveDefinition = (form: EditForm) => {
    saveMutation.mutate({ id: editingDef?.id ?? null, form })
  }

  // ─── Admin guard ──────────────────────────────────────────────────────────

  if (!isAdmin) {
    return (
      <div className="p-6">
        <PageHeader
          title="Dienstdefinitionen"
          breadcrumbs={[
            { label: 'Dispo-Einstellungen', to: '/einstellungen/dispo' },
            { label: 'Dienstdefinitionen' },
          ]}
        />
        <div className="mt-6">
          <ErrorState
            title="Kein Zugriff"
            description="Nur Administratoren haben Zugriff auf diese Seite."
          />
        </div>
      </div>
    )
  }

  // ─── Loading / error for main data ────────────────────────────────────────

  if (definitionsQuery.isLoading || settingsQuery.isLoading) {
    return <LoadingState />
  }

  if (definitionsQuery.error) {
    const errMsg = extractErrorMessage(definitionsQuery.error)
    return (
      <ErrorState
        title="Fehler beim Laden"
        description={errMsg}
        onRetry={() => { void definitionsQuery.refetch() }}
      />
    )
  }

  const definitions = definitionsQuery.data ?? []
  const tanssData = tanssQuery.data
  const tanssLoading = tanssQuery.isLoading
  const tanssError = !!tanssQuery.error

  const customCustomTypes = (tanssData?.customTypes ?? []).filter((t) => t.type === 'CUSTOM')
  const activeCustomCustomTypes = customCustomTypes.filter((t) => t.active)

  const columns = buildColumns(tanssData?.departments ?? [], customCustomTypes)

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Dienstdefinitionen"
        breadcrumbs={[
          { label: 'Dispo-Einstellungen', to: '/einstellungen/dispo' },
          { label: 'Dienstdefinitionen' },
        ]}
      />

      {/* ─── Globale Planungseinstellung ─────────────────────────────────── */}
      <Card>
        <Card.Header>
          <h2 className="text-h3">Globale Vorschlags-Einstellung</h2>
          <p className="text-caption text-gray-500 mt-1">
            Legt den globalen Custom-Typ fest, der für automatische TANSS-Planungsvorschläge verwendet wird.
          </p>
        </Card.Header>
        <Card.Body>
          <div className="flex items-end gap-4">
            <div className="flex-1 max-w-sm">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vorschlags-Typ (TANSS)
              </label>
              <SelectRoot
                value={proposalTypeValue}
                onValueChange={setProposalTypeValue}
                disabled={tanssLoading || tanssError}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="— Keinen Typ wählen —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={CUSTOM_TYPE_NONE}>— Keinen Typ wählen —</SelectItem>
                  {activeCustomCustomTypes.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
              {tanssError && (
                <p className="text-xs text-amber-600 mt-1">
                  TANSS nicht erreichbar – Typen nicht verfügbar
                </p>
              )}
            </div>
            <Button
              variant="primary"
              onClick={handleSaveProposalType}
              loading={saveSettingsMutation.isPending}
              disabled={tanssError && proposalTypeValue !== CUSTOM_TYPE_NONE}
            >
              Speichern
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* ─── Liste der Dienstdefinitionen ─────────────────────────────────── */}
      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-h3">Dienstdefinitionen</h2>
              <p className="text-caption text-gray-500 mt-1">
                Globale Dienste für die automatische Wochenplanung. Änderungen werden am nächsten Freitagslauf aktiv.
              </p>
            </div>
            <Button variant="primary" onClick={handleOpenCreate} className="gap-1.5">
              <Plus className="w-4 h-4" />
              Neue Definition
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          <DataTable
            data={definitions}
            columns={columns}
            getRowId={(row: ServiceDefinition) => String(row.id)}
            loading={definitionsQuery.isLoading}
            onRowClick={handleOpenEdit}
            searchable
            searchPlaceholder="Definitionen durchsuchen…"
            pagination={{ pageSize: 20 }}
          />
        </Card.Body>
      </Card>

      {/* ─── Edit Dialog ──────────────────────────────────────────────────── */}
      <EditDialog
        open={dialogOpen}
        definition={editingDef}
        tanssData={tanssData}
        tanssLoading={tanssLoading}
        tanssError={tanssError}
        onClose={() => setDialogOpen(false)}
        onSave={handleSaveDefinition}
        isSaving={saveMutation.isPending}
      />
    </div>
  )
}
